// frontend/src/components/common/GoogleSignInButton.js
//
// Shared "Sign in with Google" button for the two portals in this repo
// that use it as their password-recovery path — DeveloperPortal.js and
// AdminAccountPortal.js — since email-based password reset was removed
// (see docs/SESSION_CHANGELOG.md). The Hub buyer login has the same
// role for the same reason, via its own copy of this component in the
// wavelines-shop repo (see below).
// (ERP staff already use Google-only auth via server.js's own
// passport flow — this is a different, client-side mechanism for
// these JWT-based portals; see utils/googleAuth.js on the backend
// for why they're not unified.)
//
// CRA equivalent of wavelines-shop's GoogleSignInButton.jsx — same
// approach (Google Identity Services attaches to window.google rather
// than being importable, index.html loads the script tag globally),
// just reading process.env.REACT_APP_GOOGLE_CLIENT_ID instead of
// Vite's import.meta.env.
import React, { useEffect, useRef, useState } from 'react';
import * as api from '../../api';

const BUILD_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function waitForGoogleIdentity(timeoutMs = 6000) {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(window.google);
    const start = Date.now();
    const interval = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(interval);
        resolve(window.google);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        reject(new Error('Google Sign-In did not load — check your connection and try again'));
      }
    }, 100);
  });
}

// Renders Google's own styled button and forwards the resulting ID
// token (the `credential` string) to onCredential. A build-time client ID
// is used when present; otherwise it is fetched from the running backend.
// An OAuth client ID is public by design, unlike its secret. This runtime
// fallback prevents the Developer Portal button from disappearing merely
// because the frontend was built without a duplicate environment setting.
export default function GoogleSignInButton({ onCredential, onError, width = 300 }) {
  const containerRef = useRef(null);
  const [clientId, setClientId] = useState(BUILD_CLIENT_ID);
  const [configurationError, setConfigurationError] = useState('');

  useEffect(() => {
    if (BUILD_CLIENT_ID) return undefined;
    let cancelled = false;

    api.getGoogleSignInConfig()
      .then(({ clientId: runtimeClientId }) => {
        if (!runtimeClientId) throw new Error('Google sign-in is not configured on this server');
        if (!cancelled) setClientId(runtimeClientId);
      })
      .catch((e) => {
        if (!cancelled) {
          const message = e.message || 'Google sign-in is not configured on this server';
          setConfigurationError(message);
          onError?.(message);
        }
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;

    waitForGoogleIdentity()
      .then((google) => {
        if (cancelled || !containerRef.current) return;
        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => onCredential?.(response.credential),
        });
        google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width,
        });
      })
      .catch((e) => {
        if (!cancelled) { setConfigurationError(e.message); onError?.(e.message); }
      });

    return () => { cancelled = true; };
  }, [clientId, onCredential, onError, width]);

  if (!clientId) {
    if (configurationError) {
      return <p style={{ margin: '0 0 12px', color: '#b91c1c', fontSize: 12, textAlign: 'center' }}>{configurationError}</p>;
    }
    return <button type="button" disabled style={{ width, maxWidth: '100%', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', color: '#6b7280' }}>Loading Google sign-in…</button>;
  }
  return <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center' }} />;
}
